import {
  useCheckoutContext,
  useSelector,
} from '@primer-io/shared-library/contexts';
import { Fragment, FunctionalComponent } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { createFocusTrap, FocusTrap } from 'focus-trap';

import { Icons } from '../../assets/icons/Icons';
import { AnimatePresence } from '../../utils/AnimatePresence';
import { usePresence } from '../../utils/AnimatePresence/use-presence';

const createOverlayContainer = () => {
  const overlay = window.document.createElement('div');
  overlay.className = 'PrimerCheckout';
  overlay.style.position = 'fixed';
  overlay.style.zIndex = '100000';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100vw';
  overlay.style.height = '100vh';

  return overlay;
};

type ContentProps = {
  paymentMethodName?: string;
  logoSrc?: string;
  logoAltLabel?: string;
  onCloseClick?: () => void;
  onFocusClick?: () => void;
};

export const Content: FunctionalComponent<ContentProps> = ({
  paymentMethodName,
  logoSrc,
  logoAltLabel,
  onCloseClick,
  onFocusClick,
}) => {
  const labels = useSelector((s) => s.translation);
  const { className } = useCheckoutContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isPresent, safeToRemove] = usePresence();

  useEffect(() => {
    setIsVisible(isPresent);

    if (!isPresent) {
      setTimeout(() => safeToRemove?.(), 500);
    }
  }, [isPresent]);

  return (
    <div class={className('popupOverlay', { visible: isVisible })}>
      <div class={className('popupOverlayContent', { visible: isVisible })}>
        <button
          class={className('popupOverlayCloseButton', { visible: isVisible })}
          data-testId='PaymentMethodPopupOverlay.closeButton'
          alt={labels?.paymentMethodPopupOverlayClose}
          onClick={onCloseClick}
        >
          {Icons.x}
        </button>

        {logoSrc && logoAltLabel && (
          <img
            class={className('popupOverlayContent__logo')}
            src={logoSrc}
            alt={logoAltLabel}
            data-testId={'PaymentMethodPopupOverlay.logoImg'}
          />
        )}
        <div class={className('popupOverlayContent__description')}>
          {labels?.paymentMethodPopupOverlayDescription.replace(
            '{{paymentMethodName}}',
            paymentMethodName ?? '',
          )}
        </div>
        <button
          class={className('popupOverlayContent__focusButton')}
          data-testId='PaymentMethodPopupOverlay.focusButton'
          onClick={onFocusClick}
        >
          {labels?.paymentMethodPopupOverlayBringBackThePaymentPage}
        </button>
      </div>
    </div>
  );
};

type ContainerProps = {
  isVisible: boolean;
} & ContentProps;

const Container: FunctionalComponent<ContainerProps> = ({
  isVisible,
  onCloseClick,
  ...otherProps
}) => {
  const focusTrap = useRef<FocusTrap | null>(null);

  const overlayContainerRef = useRef<HTMLDivElement>();

  if (!overlayContainerRef.current) {
    const overlay = createOverlayContainer();
    window.document.body.appendChild(overlay);
    overlayContainerRef.current = overlay;
  }

  overlayContainerRef.current.style.pointerEvents = isVisible ? 'all' : 'none';

  useLayoutEffect(() => {
    if (isVisible) {
      focusTrap.current = createFocusTrap(overlayContainerRef.current, {
        onDeactivate: onCloseClick,
        returnFocusOnDeactivate: true,
        clickOutsideDeactivates: false,
        allowOutsideClick: false,
      });
      focusTrap.current.activate();
    } else {
      focusTrap.current?.deactivate({ onDeactivate: undefined });
      focusTrap.current = null;
    }
  }, [isVisible]);

  return (
    <Fragment>
      {createPortal(
        <div>
          <AnimatePresence>
            {[
              isVisible && (
                <Content
                  key={'key'} /* Fake key mandatory of AnimatePresence */
                  onCloseClick={onCloseClick}
                  {...otherProps}
                />
              ),
            ]}
          </AnimatePresence>
        </div>,
        overlayContainerRef.current,
      )}
    </Fragment>
  );
};

const PaymentMethodPopupOverlay = () => {
  const props = useSelector((state) => state.paymentMethodPopupOverlay);

  return <Container {...props} />;
};

export default PaymentMethodPopupOverlay;
